import { useEffect, useMemo, useState } from 'react';
import { SortDirection, getTodayActivity } from '@api/cut';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@features/redux/store';
import { CutReportLayout } from '@components/reports/CutReportLayout';
import { useAlert } from '@hooks/useAlert';
import { useNameMapQuery } from '@features/redux/apis/org';
import { setLastUpdated } from '@features/redux/slices/reportSlice';
import { TotalCuts } from './TotalCuts';

const PAGE_SIZE = 250;

type TodayCuts = {
    TotalRecords: number;
    ResellerCompanyName: string;
    LocationName: string;
    ParentCompanyName: string;
    totalCuts: number;
    successfulCuts: number;
    warrantyClaimCuts: number;
};

export type FilterInput = {
    companies: { [key: string]: boolean };
    resellers: { [key: string]: boolean };
    locations: { [key: string]: boolean };
    cutType: { [key: string]: boolean };
};

export const TodaysCutReport = () => {
    const [todayCutData, setTodayCutData] = useState<TodayCuts[]>([]);
    const [loadingCuts, setLoadingCuts] = useState<boolean>(false);

    const dispatch = useDispatch();

    const [filters, setFilters] = useState<FilterInput>({
        companies: {},
        resellers: {},
        locations: {},
        cutType: {},
    });

    const { data: nameMap } = useNameMapQuery({});

    const [pageNum, setPageNum] = useState<number>(1);
    const [sortOptions, setSortOptions] = useState<
        Record<'company' | 'location' | 'reseller' | 'totalcuts', SortDirection>
    >({
        company: SortDirection.Ascending,
        location: SortDirection.Ascending,
        reseller: SortDirection.Ascending,
        totalcuts: SortDirection.Descending,
    });
    const [priority, setPriority] = useState<'totalcuts' | 'org'>('totalcuts');

    const setFilter = (key: keyof FilterInput, value: string, selectOne?: boolean) => {
        const isActive = filters[key][value];
        setFilters({ ...filters, [key]: { ...(selectOne ? {} : filters[key]), [value]: !isActive } });
        setPageNum(1);
    };

    const setSortDirection = (key: 'company' | 'location' | 'reseller' | 'totalcuts', value: SortDirection) => {
        setSortOptions({ ...sortOptions, [key]: value });
        setPageNum(1);

        if (key === 'totalcuts') {
            setPriority('totalcuts');
            return;
        }

        setPriority('org');
    };

    const { IdToken } = useSelector((state: RootState) => state.auth);
    const { setAlert } = useAlert();

    // ! Todo: Refactor to use RTK Query
    const getCutData = async () => {
        setLoadingCuts(true);
        try {
            const todaysCuts = (await getTodayActivity({
                token: IdToken!,
                params: {
                    pageSize: PAGE_SIZE,
                    pageNum: pageNum - 1,
                    companyIds: Object.keys(filters.companies)
                        .filter((key) => filters.companies[key])
                        .join(','),
                    resellerIds: Object.keys(filters.resellers)
                        .filter((key) => filters.resellers[key])
                        .join(','),
                    locationIds: Object.keys(filters.locations)
                        .filter((key) => filters.locations[key])
                        .join(','),
                    type: Object.keys(filters.cutType).find((key) => filters.cutType[key]) || undefined,
                    sortCompany: sortOptions.company,
                    sortLocation: sortOptions.location,
                    sortReseller: sortOptions.reseller,
                    sortTotalCuts: sortOptions.totalcuts,
                    priority,
                },
            })) as TodayCuts[];

            dispatch(setLastUpdated());

            setTodayCutData(todaysCuts);
        } catch (e) {
            setAlert('Could not get todays cut data', 'error');
        } finally {
            setLoadingCuts(false);
        }
    };

    useEffect(() => {
        getCutData();
    }, [pageNum, sortOptions, filters]);

    const numPages = useMemo(
        () => (todayCutData[0] ? Math.ceil(todayCutData[0].TotalRecords / PAGE_SIZE) : 1),
        [todayCutData]
    );

    return (
        <CutReportLayout
            title="Today's Cuts"
            sortOptions={sortOptions}
            setSortDirection={setSortDirection}
            filters={filters}
            setFilter={setFilter as any}
            pageNum={pageNum}
            setPageNum={setPageNum}
            note={`Total # Locations: ${todayCutData[0]?.TotalRecords || 'Unknown'}`}
            numPages={numPages}
            tableOptions={{
                headers: ['Company', 'Reseller', 'Location', 'Total Cuts', 'Successful Cuts', 'Warranty Cuts'],
                data: todayCutData.map((location, idx) => [
                    location.ParentCompanyName,
                    location.ResellerCompanyName,
                    location.LocationName,
                    location.totalCuts,
                    <TotalCuts
                        key={`total-cuts-success: ${location.warrantyClaimCuts} ${location.successfulCuts} ${idx}`}
                        totalCuts={location.totalCuts}
                        numCutType={location.successfulCuts}
                        color={'success'}
                    />,
                    <TotalCuts
                        key={`total-cuts-warranty: ${location.warrantyClaimCuts} ${location.successfulCuts} ${idx}`}
                        totalCuts={location.totalCuts}
                        numCutType={location.warrantyClaimCuts}
                        color={'error'}
                    />,
                ]),
            }}
            hasData={todayCutData.length > 0}
            loading={loadingCuts || !nameMap}
        />
    );
};
